import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { setAssistantEnabled } from '../../../services/whatsappApi';
import { switchView, toggleFailure, toggleStep } from '../../../whatsapp/assistantSwitch';
import { useInbox } from '../../../whatsapp/InboxProvider';
import { useWhatsApp } from '../../../whatsapp/WhatsAppContext';
import { Modal } from '../Modal';
import { Switch } from '../Switch';
import { Toast } from '../Toast';

type DialogKind = 'confirm_off' | 'no_topics';

const WarningIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
    <path d="M10.3 3.9 2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
  </svg>
);

const SparkIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3Z" />
  </svg>
);

const secondaryButton =
  'inline-flex h-11 items-center justify-center rounded-full border border-stone-200 bg-white px-6 text-sm font-semibold text-stone-800 hover:bg-stone-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:opacity-60';

/** The assistant on/off pill of the inbox header, with its confirmation dialogs (flow 8). */
export const AssistantSwitchControl = () => {
  const { account, applyAccount, refreshAccount } = useWhatsApp();
  const { pollNow } = useInbox();
  const view = switchView(account);

  const [dialog, setDialog] = useState<DialogKind | null>(null);
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const rootRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const ids = useId();
  const statusId = `${ids}-estado`;

  // The dialog is gone (and the switch may have been re-rendered as another variant):
  // the keyboard user lands back on the switch.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (dialog === null && wasOpen.current) {
      rootRef.current?.querySelector<HTMLElement>('[role="switch"]')?.focus();
    }
    wasOpen.current = dialog !== null;
  }, [dialog]);

  const close = useCallback(() => {
    setDialog(null);
    setDialogError(null);
  }, []);
  // Stable: Toast restarts its timer whenever this callback changes.
  const dismissToast = useCallback(() => setToast(null), []);

  const run = async (enabled: boolean, fromDialog: boolean) => {
    setBusy(true);
    setDialogError(null);
    try {
      applyAccount(await setAssistantEnabled(enabled));
      setDialog(null);
      // Turning off hands pending conversations to the team on the server: show it now.
      if (!enabled) pollNow();
    } catch (err) {
      const failure = toggleFailure(err);
      if (failure.kind === 'no_topics') {
        setDialog('no_topics');
        void refreshAccount();
      } else if (failure.kind === 'disconnected') {
        setDialog(null);
        void refreshAccount();
      } else if (fromDialog) {
        setDialogError(failure.text);
      } else {
        setToast(failure.text);
      }
    } finally {
      setBusy(false);
    }
  };

  const onToggle = () => {
    const step = toggleStep(account);
    if (step === 'confirm_off') setDialog('confirm_off');
    else if (step === 'no_topics') setDialog('no_topics');
    else if (step === 'enable') void run(true, false);
  };

  const pillBase = 'flex h-10 items-center gap-2.5 rounded-full border text-sm';

  return (
    <div ref={rootRef} className="contents">
      {view === 'off' ? (
        <div className={`${pillBase} border-amber-200 bg-amber-100 pl-3.5 pr-2 text-amber-900`}>
          <WarningIcon />
          <span id={statusId}>
            <span className="font-semibold">Asistente apagado:</span> nadie responde automáticamente
          </span>
          <Switch checked={false} label="Asistente" describedBy={statusId} busy={busy} onChange={onToggle} />
        </div>
      ) : (
        <div className={`${pillBase} border-stone-200 bg-white pl-4 pr-2 font-medium text-stone-800`}>
          <span>Asistente</span>
          {view === 'on' ? (
            <>
              <Switch checked label="Asistente activo" busy={busy} onChange={onToggle} />
              <span className="pr-2 font-mono text-xs text-blue-700 max-sm:hidden">ON</span>
            </>
          ) : (
            <>
              <Switch checked={false} label="Asistente apagado" disabled onChange={onToggle} />
              <span className="pr-2 font-mono text-xs text-stone-600 max-sm:hidden">OFF</span>
            </>
          )}
        </div>
      )}

      {dialog === 'confirm_off' && (
        <Modal
          titleId={`${ids}-t-apagar`}
          descriptionId={`${ids}-d-apagar`}
          maxWidth="max-w-[440px]"
          locked={busy}
          onClose={close}
          initialFocus={cancelRef}
        >
          <h2 id={`${ids}-t-apagar`} className="text-xl font-bold text-stone-900">
            ¿Apagar el asistente?
          </h2>
          <p id={`${ids}-d-apagar`} className="text-sm leading-[1.55] text-stone-600">
            Dejará de responder automáticamente a todos los pacientes de tu clínica. Los mensajes nuevos esperarán al
            equipo en la bandeja.
          </p>
          {dialogError && (
            <p role="alert" className="text-sm text-red-700">
              {dialogError}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-1.5">
            <button ref={cancelRef} type="button" disabled={busy} onClick={close} className={secondaryButton}>
              Cancelar
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void run(false, true)}
              className="inline-flex h-11 items-center justify-center rounded-full bg-red-700 px-6 text-sm font-semibold text-white hover:bg-red-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Apagar asistente
            </button>
          </div>
        </Modal>
      )}

      {dialog === 'no_topics' && (
        <Modal
          titleId={`${ids}-t-sin-temas`}
          descriptionId={`${ids}-d-sin-temas`}
          maxWidth="max-w-[480px]"
          locked={false}
          onClose={close}
          initialFocus={cancelRef}
        >
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-100 text-amber-800">
            <SparkIcon />
          </span>
          <h2 id={`${ids}-t-sin-temas`} className="text-xl font-bold text-stone-900">
            Activa al menos un tema para encender el asistente
          </h2>
          <div id={`${ids}-d-sin-temas`} className="flex flex-col gap-2 text-sm leading-[1.55] text-stone-600">
            <p>
              El asistente solo responde sobre los temas que tienes activos, por ejemplo horarios y ubicación o
              servicios y precios.
            </p>
            <p>
              Ahora no hay ninguno activo, así que todos los mensajes pasarían al equipo. Agrega el contenido de un tema
              y actívalo para empezar.
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-1.5">
            <button ref={cancelRef} type="button" onClick={close} className={secondaryButton}>
              Cancelar
            </button>
            <Link
              to="/whatsapp/settings/assistant"
              className="inline-flex h-11 items-center justify-center rounded-full bg-blue-600 px-6 text-sm font-semibold text-white no-underline hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600"
            >
              Configurar
            </Link>
          </div>
        </Modal>
      )}

      {toast && <Toast message={toast} onDismiss={dismissToast} />}
    </div>
  );
};
